import { getClient } from './currentScopes.js';
import { DEBUG_BUILD } from './debug-build.js';
import { logger } from './utils-hoist/logger.js';
var installedIntegrations = [];
/** Map of integrations assigned to a client */

/**
 * Remove duplicates from the given array, preferring the last instance of any duplicate. Not guaranteed to
 * preserve the order of integrations in the array.
 *
 * @private
 */

function filterDuplicates(integrations) {
  var integrationsByName = {};
  integrations.forEach(currentInstance => {
    var {
      name
    } = currentInstance;
    var existingInstance = integrationsByName[name]; // We want integrations later in the array to overwrite earlier ones of the same type, except that we never want a
    // default instance to overwrite an existing user instance

    if (existingInstance && !existingInstance.isDefaultInstance && currentInstance.isDefaultInstance) {
      return;
    }

    integrationsByName[name] = currentInstance;
  });
  return Object.values(integrationsByName);
}
/** Gets integrations to install */


function getIntegrationsToSetup(options) {
  var defaultIntegrations = options.defaultIntegrations || [];
  var userIntegrations = options.integrations; // We flag default instances, so that later we can tell them apart from any user-created instances of the same class

  defaultIntegrations.forEach(integration => {
    integration.isDefaultInstance = true;
  });
  var integrations;

  if (Array.isArray(userIntegrations)) {
    integrations = [...defaultIntegrations, ...userIntegrations];
  } else if (typeof userIntegrations === 'function') {
    var resolvedUserIntegrations = userIntegrations(defaultIntegrations);
    integrations = Array.isArray(resolvedUserIntegrations) ? resolvedUserIntegrations : [resolvedUserIntegrations];
  } else {
    integrations = defaultIntegrations;
  }

  return filterDuplicates(integrations);
}
/**
 * Given a list of integration instances this installs them all. When `withDefaults` is set to `true` then all default
 * integrations are added unless they were already provided before.
 * @param integrations array of integration instances
 * @param withDefault should enable default integrations
 */


function setupIntegrations(client, integrations) {
  var integrationIndex = {};
  integrations.forEach(integration => {
    // guard against empty provided integrations
    if (integration) {
      setupIntegration(client, integration, integrationIndex);
    }
  });
  return integrationIndex;
}
/**
 * Execute the `afterAllSetup` hooks of the given integrations.
 */


function afterSetupIntegrations(client, integrations) {
  for (var integration of integrations) {
    // guard against empty provided integrations
    if (integration !== null && integration !== void 0 && integration.afterAllSetup) {
      integration.afterAllSetup(client);
    }
  }
}
/** Setup a single integration.  */


function setupIntegration(client, integration, integrationIndex) {
  if (integrationIndex[integration.name]) {
    DEBUG_BUILD && logger.log("Integration skipped because it was already installed: ".concat(integration.name));
    return;
  }

  integrationIndex[integration.name] = integration; // `setupOnce` is only called the first time

  if (installedIntegrations.indexOf(integration.name) === -1 && typeof integration.setupOnce === 'function') {
    integration.setupOnce();
    installedIntegrations.push(integration.name);
  } // `setup` is run for each client


  if (integration.setup && typeof integration.setup === 'function') {
    integration.setup(client);
  }

  if (typeof integration.preprocessEvent === 'function') {
    var callback = integration.preprocessEvent.bind(integration);
    client.on('preprocessEvent', (event, hint) => callback(event, hint, client));
  }

  if (typeof integration.processEvent === 'function') {
    var _callback = integration.processEvent.bind(integration);

    var processor = Object.assign((event, hint) => _callback(event, hint, client), {
      id: integration.name
    });
    client.addEventProcessor(processor);
  }

  DEBUG_BUILD && logger.log("Integration installed: ".concat(integration.name));
}
/** Add an integration to the current scope's client. */


function addIntegration(integration) {
  var client = getClient();

  if (!client) {
    DEBUG_BUILD && logger.warn("Cannot add integration \"".concat(integration.name, "\" because no SDK Client is available."));
    return;
  }

  client.addIntegration(integration);
}
/**
 * Define an integration function that can be used to create an integration instance.
 * Note that this by design hides the implementation details of the integration, as they are considered internal.
 */


function defineIntegration(fn) {
  return fn;
}

export { addIntegration, afterSetupIntegrations, defineIntegration, getIntegrationsToSetup, installedIntegrations, setupIntegration, setupIntegrations };